import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';


const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: 'Destinations',
      links: [
        { label: 'Asia Tours', href: '/search-results?region=asia' },
        { label: 'Europe Tours', href: '/search-results?region=europe' },
        { label: 'Americas Tours', href: '/search-results?region=americas' },
        { label: 'Africa Tours', href: '/search-results?region=africa' },
        { label: 'Oceania Tours', href: '/search-results?region=oceania' },
        { label: 'All Destinations', href: '/search-results' }
      ]
    },
    {
      title: 'Tour Types',
      links: [
        { label: 'Adventure Tours', href: '/search-results?category=adventure' },
        { label: 'Cultural Tours', href: '/search-results?category=cultural' },
        { label: 'Food & Drink', href: '/search-results?category=food' },
        { label: 'Nature & Wildlife', href: '/search-results?category=nature' },
        { label: 'City Tours', href: '/search-results?category=city' },
        { label: 'Beach Tours', href: '/search-results?category=beach' }
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '/help' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'Booking Help', href: '/booking-help' },
        { label: 'Cancellation Policy', href: '/cancellation' },
        { label: 'Travel Insurance', href: '/insurance' },
        { label: 'Safety Guidelines', href: '/safety' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About TourEase', href: '/about' },
        { label: 'Careers', href: '/careers' },
        { label: 'Press', href: '/press' },
        { label: 'Partner with Us', href: '/partners' },
        { label: 'Affiliate Program', href: '/affiliates' },
        { label: 'Blog', href: '/blog' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', href: 'https://facebook.com/tourease' },
    { name: 'Twitter', icon: 'Twitter', href: 'https://twitter.com/tourease' },
    { name: 'Instagram', icon: 'Instagram', href: 'https://instagram.com/tourease' },
    { name: 'YouTube', icon: 'Youtube', href: 'https://youtube.com/tourease' },
    { name: 'LinkedIn', icon: 'Linkedin', href: 'https://linkedin.com/company/tourease' }
  ];

  const paymentMethods = [
    { name: 'Visa', icon: 'CreditCard' },
    { name: 'Mastercard', icon: 'CreditCard' },
    { name: 'PayPal', icon: 'Wallet' },
    { name: 'Apple Pay', icon: 'Smartphone' },
    { name: 'Google Pay', icon: 'Smartphone' }
  ];

  const handleLinkClick = (href) => {
    if (href?.startsWith('http')) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(href);
    }
  };

  const handleLogoClick = () => {
    navigate('/homepage');
  };

  return (
    <footer className="bg-foreground text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <button 
              onClick={handleLogoClick}
              className="flex items-center space-x-2 mb-6 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                <Icon name="Compass" size={24} color="white" />
              </div>
              <span className="text-2xl font-bold">TourEase</span>
            </button>
            
            <p className="text-white/80 mb-6 leading-relaxed">
              Discover the world's most amazing destinations with carefully curated tours and experiences. 
              Your adventure starts here with TourEase.
            </p>

            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={16} className="text-primary" />
                <span className="text-white/80">+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={16} className="text-primary" />
                <span className="text-white/80">hello@tourease.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span className="text-white/80">San Francisco, CA</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks?.map((social) => (
                <button
                  key={social?.name}
                  onClick={() => handleLinkClick(social?.href)}
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-colors"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={18} />
                </button>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections?.map((section, index) => (
            <div key={index}>
              <h3 className="font-semibold text-white mb-4">{section?.title}</h3>
              <ul className="space-y-3">
                {section?.links?.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <button
                      onClick={() => handleLinkClick(link?.href)}
                      className="text-white/70 hover:text-white transition-colors text-sm"
                    >
                      {link?.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom Footer */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
            {/* Copyright */}
            <div className="text-center lg:text-left">
              <p className="text-white/70 text-sm">
                © {currentYear} TourEase. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start items-center space-x-6 mt-2">
                <button
                  onClick={() => handleLinkClick('/privacy')}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => handleLinkClick('/terms')}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                >
                  Terms of Service
                </button>
                <button
                  onClick={() => handleLinkClick('/cookies')}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                >
                  Cookie Policy
                </button>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="text-center lg:text-right">
              <p className="text-white/70 text-sm mb-3">Secure payments with</p>
              <div className="flex items-center justify-center lg:justify-end space-x-3">
                {paymentMethods?.map((method, index) => (
                  <div
                    key={index}
                    className="w-10 h-6 bg-white/10 rounded flex items-center justify-center"
                    title={method?.name}
                  >
                    <Icon name={method?.icon} size={14} className="text-white/70" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center space-x-8 mt-8 pt-8 border-t border-white/10">
            <div className="flex items-center space-x-2 text-white/70">
              <Icon name="Shield" size={16} className="text-primary" />
              <span className="text-sm">SSL Secured</span>
            </div>
            <div className="flex items-center space-x-2 text-white/70">
              <Icon name="Award" size={16} className="text-primary" />
              <span className="text-sm">Best Travel Platform 2024</span>
            </div>
            <div className="flex items-center space-x-2 text-white/70">
              <Icon name="Users" size={16} className="text-primary" />
              <span className="text-sm">50,000+ Happy Travelers</span>
            </div>
            <div className="flex items-center space-x-2 text-white/70">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-sm">24/7 Support</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;